import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import { LogOut, LayoutDashboard, Briefcase, Calendar, Inbox } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";

const nav = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/services", label: "Services", icon: Briefcase },
  { to: "/admin/bookings", label: "Bookings", icon: Calendar },
  { to: "/admin/leads", label: "Leads", icon: Inbox },
];

export function AdminShell() {
  const { user, checked, logout } = useAuth();
  const navigate = useNavigate();

  if (checked && (!user || user.role !== "admin")) return <Navigate to="/admin/login" replace />;
  if (!checked) return <div className="min-h-screen grid place-items-center text-slate2">Loading…</div>;

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-ivory text-charcoal flex" data-testid="admin-shell">
      {/* SIDEBAR */}
      <aside className="w-64 bg-charcoal text-ivory flex flex-col" data-scope="admin">
        <div className="p-6 border-b border-[#333]">
          <Link to="/"><Logo variant="light" /></Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm font-mono uppercase tracking-[0.16em] transition-colors ${
                  isActive ? "bg-gold text-charcoal" : "text-[#D8D5CE] hover:bg-[#1c1c1c]"
                }`
              }
              data-testid={`admin-nav-${n.label.toLowerCase()}`}
            >
              <n.icon size={14} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#333]">
          <div className="text-xs text-[#9CA3AF] mb-2">{user?.email}</div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-[#D8D5CE] hover:text-gold" data-testid="admin-logout">
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <main className="flex-1 p-8 lg:p-12 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}

export function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/bookings").then((r) => setBookings(r.data.slice(0, 6))).catch(() => {});
  }, []);

  const cards = stats ? [
    { k: "Requests", v: stats.total_bookings },
    { k: "Pending", v: stats.requested_bookings },
    { k: "Confirmed", v: stats.confirmed_bookings },
    { k: "Completed", v: stats.completed_bookings },
    { k: "Leads", v: stats.leads },
    { k: "Services", v: stats.services },
  ] : [];

  return (
    <div data-testid="admin-overview">
      <div className="eyebrow">Practice Desk</div>
      <h1 className="font-display text-4xl md:text-5xl mt-4 tracking-tight">Overview</h1>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-10">
        {cards.map((c) => (
          <div key={c.k} className="border border-[#E0DCD3] bg-white p-6" data-testid={`stat-${c.k.toLowerCase().replace(/\s|₹|\(|\)/g, '')}`}>
            <div className="eyebrow">{c.k}</div>
            <div className="font-display text-4xl mt-3">{c.v}</div>
          </div>
        ))}
      </div>

      <div className="mt-14">
        <div className="eyebrow">Recent requests</div>
        <div className="mt-4 border border-[#E0DCD3] bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate2 font-mono uppercase text-[10px] tracking-[0.24em] border-b border-[#E0DCD3]">
                <th className="p-4">Client</th>
                <th className="p-4">Service</th>
                <th className="p-4">Preferred slot</th>
                <th className="p-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} className="border-b border-[#E0DCD3] last:border-0">
                  <td className="p-4">{b.client_name}<div className="text-xs text-slate2">{b.client_email}</div></td>
                  <td className="p-4">{b.service_title}</td>
                  <td className="p-4">{new Date(b.slot_iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}</td>
                  <td className="p-4"><StatusPill status={b.status} /></td>
                </tr>
              ))}
              {bookings.length === 0 && (
                <tr><td colSpan={4} className="p-8 text-center text-slate2">No requests yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export function StatusPill({ status }) {
  const map = {
    requested: "border border-[#E0DCD3] text-slate2",
    confirmed: "bg-gold text-charcoal",
    completed: "bg-charcoal text-ivory",
    cancelled: "bg-red-100 text-red-800",
  };
  return (
    <span className={`inline-block px-3 py-1 font-mono text-[10px] uppercase tracking-[0.22em] ${map[status] || "border"}`}>
      {status?.replace("_", " ")}
    </span>
  );
}
